<script setup lang="ts">
import { computed } from 'vue'
import { mdiMenuDown, mdiMenuRight } from '@mdi/js'

import type { EditorSettingsItem } from '@/helpers/interfaces'
import type { PlanType } from '@/api/interfaces'

import LockIcon from '@/components/icons/LockIcon.vue'
import UpgradePlanButton from '@/components/editor/UpgradePlanButton.vue'

interface Props {
  title: string
  planType?: PlanType
  items: EditorSettingsItem[]
}

const props = defineProps<Props>()

const hasDisabledEditorSettings = computed(() => props.items.find((item) => item.disabled))
</script>

<template>
  <div class="editor-settings">
    <h3 v-if="items.length" class="editor-settings-title">{{ title }}</h3>
    <v-expansion-panels class="editor-settings-list" multiple>
      <template v-for="(item, index) in items" :key="index">
        <v-expansion-panel class="editor-settings-list-item" :class="{ disabled: item.disabled }">
          <v-expansion-panel-title :expand-icon="mdiMenuRight" :collapse-icon="mdiMenuDown">
            <div class="editor-settings-list-item-header">
              {{ item.title }}
              <LockIcon v-if="item.disabled" color="red" />
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <p class="editor-settings-description" v-if="item.description">
              {{ item.description }}
            </p>
            <component :is="item.component" />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </template>
    </v-expansion-panels>
    <UpgradePlanButton v-if="hasDisabledEditorSettings && planType" :plan="planType" />
  </div>
</template>

<style lang="scss" scoped>
.editor-settings {
  margin: 32px 0;
}

.editor-settings-title {
  font-size: 24px;
  font-weight: 700;
}

.editor-settings-description {
  margin-bottom: 16px;
}

.editor-settings-list {
  margin-top: 12px;
}

.v-expansion-panel {
  background-color: var(--lightest);

  &:not(:first-child)::after {
    border: 0;
  }

  &--active:not(:first-child),
  &--active + .v-expansion-panel {
    margin-top: 0;
  }
}

:deep(.v-expansion-panel__shadow) {
  box-shadow: none;
}

.v-expansion-panel-title {
  justify-content: flex-start;
  align-items: center;
  padding: 0;
  min-height: 44px !important;
  font-size: 20px;
  font-weight: 700;

  &:hover,
  &--active {
    :deep(.v-expansion-panel-title__overlay) {
      opacity: 0;
    }
  }
}

.editor-settings-list-item {
  &.disabled {
    color: var(--gray-light-a7);
  }
}

.editor-settings-list-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

:deep(.v-expansion-panel-title__icon) {
  order: -1;
  margin-inline-start: 0;
  margin-right: 8px;
}

:deep(.v-expansion-panel-text__wrapper) {
  padding: 0;

  @include media('md-and-up') {
    padding: 0 0 0 36px;
  }
}
</style>
